<template>

  <v-container fluid class="order-document-uploader">

    <v-row class="">
      <!--------------------->
      <!-- Supporting Docs -->
      <!--------------------->
      <v-col align="center" class="pb-8">
        <!-- Dialog -->
        <v-dialog persistent fullscreen v-model="isSupportingDocsDialogOpen" align="center">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text color="primary" v-bind="attrs" v-on="on">
              + Add Supporting Docs
            </v-btn>
          </template>
          <v-card>
            <v-container>
              <v-row class="mt-1 mb-1">
                <v-col align="center">
                  <v-btn text @click="updateSupportingDocs">Close</v-btn>
                </v-col>
              </v-row>
              <file-attachment-manager
                  ref="doc-manager-supporting"
                  titleText="Supporting Documents"
                  fileDisplayWidth="300px"
                  :allowedFileTypes="$root.allowedFileTypesForUploads" />
            </v-container>
          </v-card>
        </v-dialog>

        <!-- Info -->
        <div class="mt-3 description-text">
          Documents will be included with order invoice
        </div>

        <!-- Added count -->
        <div class="mt-6" v-if="addedSupportingDocs">
          <v-icon class="mr-2 pb-1" color="white">mdi-file-multiple</v-icon>
          <span v-if="addedSupportingDocs.length === 1">{{ addedSupportingDocs.length }} document</span>
          <span v-else>{{ addedSupportingDocs.length }} documents</span>
        </div>
      </v-col>

      <!----------------------->
      <!-- (Attachment Docs) -->
      <!----------------------->
      <v-col align="center">
        <!-- Dialog -->
        <v-dialog persistent fullscreen v-model="isAttachmentDocsDialogOpen" align="center">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text color="primary" v-bind="attrs" v-on="on">
              + Add Attachment Docs
            </v-btn>
          </template>
          <v-card>
            <v-container>
              <v-row class="mt-1 mb-1">
                <v-col align="center">
                  <v-btn text @click="updateAttachmentDocs">Close</v-btn>
                </v-col>
              </v-row>
              <file-attachment-manager
                  ref="doc-manager-attachment"
                  titleText="Attachment Documents"
                  fileDisplayWidth="300px"
                  :allowedFileTypes="$root.allowedFileTypesForUploads" />
            </v-container>
          </v-card>
        </v-dialog>

        <!-- Info -->
        <div class="mt-3 description-text">
          Documents will be available separately as attachments
        </div>

        <!-- Added count -->
        <div class="mt-6" v-if="addedAttachmentDocs">
          <v-icon style="" class="mr-2 pb-1" color="white">mdi-file-multiple</v-icon>
          <span v-if="addedAttachmentDocs.length === 1">{{ addedAttachmentDocs.length }} document</span>
          <span v-else>{{ addedAttachmentDocs.length }} documents</span>
        </div>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
import FileAttachmentManager from '@/components/app/FileAttachmentManager'

export default {
  name: 'OrderDocumentUploader',

  components: {
    FileAttachmentManager
  },

  data () {
    return {
      // ------------- Supporting documents
      isSupportingDocsDialogOpen: false,
      addedSupportingDocs: null,
      // ------------- Attachment documents
      isAttachmentDocsDialogOpen: false,
      addedAttachmentDocs: null
    }
  },

  computed: {
    hasSupportingDocs () {
      return this.addedSupportingDocs && this.addedSupportingDocs.length > 0
    },
    hasAttachmentDocs () {
      return this.addedAttachmentDocs && this.addedAttachmentDocs.length > 0
    }
  },

  methods: {
    // Handle supporting docs update
    updateSupportingDocs () {
      this.isSupportingDocsDialogOpen = false
      const docManager = this.$refs['doc-manager-supporting']
      const docs = (docManager) ? docManager.getAttachments() : null
      this.addedSupportingDocs = docs
    },

    // Handle attachment docs update
    updateAttachmentDocs () {
      this.isAttachmentDocsDialogOpen = false
      const docManager = this.$refs['doc-manager-attachment']
      const docs = (docManager) ? docManager.getAttachments() : null
      this.addedAttachmentDocs = docs
    },

    // Perform upload of supporting docs
    async uploadSupportingDocs (orderId) {
      const errors = []
      let uploaded = 0

      if (!this.hasSupportingDocs || !orderId) return { errors, uploaded }

      for (const doc of this.addedSupportingDocs) {
        const assetKey = doc.asset_key
        const file = doc.file

        if (assetKey && file) {
          let uploadInfo = null
          try {
            // Generate upload URL
            uploadInfo = await this.$store.dispatch('sh/dataApi', {
              method: 'get',
              uri: `order/${orderId}/asset/uploadUrl`,
              to_model: false,
              opts: {
                params: { asset_key: assetKey }
              }
            })

            // Upload document
            const result = await fetch(uploadInfo.url, { method: 'PUT', body: file, signal: AbortSignal.timeout(55000) })

            // Record issues saving to storage
            if (result && result.status !== 200) {
              const issueInfo = `Issue uploading: ${doc.file_name} - ${result.statusText}`
              errors.push(issueInfo)
            } else {
              ++uploaded
            }
          } catch (err) {
            const errMessage = err.message || err.text
            const errInfo = `Error performing upload: ${doc.file_name} - ${errMessage}`
            errors.push(errInfo)
          }
        }
      }

      return { uploaded, errors }
    },

    // Perform upload of attachment docs
    async uploadAttachmentDocs (orderId) {
      const errors = []
      let uploaded = 0

      if (!this.hasAttachmentDocs || !orderId) return { errors, uploaded }

      for (const doc of this.addedAttachmentDocs) {
        const assetKey = doc.asset_key
        const file = doc.file

        if (assetKey && file) {
          let uploadInfo = null
          try {
            // Generate upload URL
            uploadInfo = await this.$store.dispatch('sh/dataApi', {
              method: 'get',
              uri: `order/${orderId}/asset/uploadUrl`,
              to_model: false,
              opts: {
                params: { asset_key: assetKey }
              }
            })

            // Upload document
            const result = await fetch(uploadInfo.url, { method: 'PUT', body: file, signal: AbortSignal.timeout(55000) })

            // Record issues saving to storage
            if (result && result.status !== 200) {
              const issueInfo = `Issue uploading: ${doc.file_name} - ${result.statusText}`
              errors.push(issueInfo)
            } else {
              ++uploaded
            }
          } catch (err) {
            const errMessage = err.message || err.text
            const errInfo = `Error performing upload: ${doc.file_name} - ${errMessage}`
            errors.push(errInfo)
          }
        }
      }

      return { uploaded, errors }
    }
  }
}
</script>

<style scoped>
</style>
